import React from 'react';
import { styled } from 'styled-components';
import { Box, Typography } from '@mui/material';
import Button from '../../../common/buttons/Button';

interface WelcomeStepProps {
	onStart: () => void;
	companyName?: string;
}

export default function WelcomeStep({
	onStart,
	companyName = 'PRODUCER/PRODCO',
}: WelcomeStepProps) {
	return (
		<WelcomeContainer>
			<WelcomeParagraph>
				On behalf of {companyName}, we are thrilled to tighten up the
				information in our crew database in an effort to increase
				fidelity and efficiency while working with our most common and
				trusted partners. This is one of many initiatives on the Orbito
				platform to continue to create a more seamless experience across
				all phases of production and post-production.
			</WelcomeParagraph>

			<WelcomeParagraph>
				Full participation in this form will significantly increase and
				confirm the likelihood that we can not only work effectively
				together on projects but that {companyName} can quickly and
				efficiently hire and access you for a variety of projects that
				fit your desired roles and positions. In order to be eligible to
				work with producer all fields must be completed. Please know
				that data security is an absolute non-negotiable for us. We want
				you to know that we use end-to-end encrypted data security for
				the information you share in this form and all future data. Your
				personal information will never be shared outside of{' '}
				{companyName}.
			</WelcomeParagraph>

			<WelcomeParagraph>
				We sincerely appreciate your participation and time spent
				filling out the details in this form. We look forward to using
				all of the information provided for a more effective hiring
				process on all the award winning work we'll do together.
			</WelcomeParagraph>

			<StartButton variant="contained" onClick={onStart}>
				START
			</StartButton>

			<PrivacyPolicySection>
				<PrivacyPolicyText>
					Information about Orbito's privacy policy and information
					sharing can be found below.
				</PrivacyPolicyText>
				<PrivacyPolicyLink
					href="/privacy-policy"
					target="_blank"
					rel="noopener noreferrer"
				>
					View Privacy Policy
				</PrivacyPolicyLink>
			</PrivacyPolicySection>
		</WelcomeContainer>
	);
}

const WelcomeContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	padding: 24px;
	height: 100%;
	justify-content: center;

	${({ theme }) => theme.breakpoints.down('sm')} {
		padding: 16px;
		justify-content: flex-start;
	}
`;

const WelcomeTitle = styled(Typography)`
	font-size: 32px;
	font-weight: bold;
	color: ${({ theme }) => theme.palette.text.primary};
	margin-bottom: 24px;

	${({ theme }) => theme.breakpoints.down('sm')} {
		font-size: 28px;
		margin-bottom: 20px;
	}
`;

const WelcomeParagraph = styled(Typography)`
	font-size: 16px;
	line-height: 1.6;
	color: ${({ theme }) => theme.palette.text.primary};
	margin-bottom: 20px;
	padding-top: 15px;

	${({ theme }) => theme.breakpoints.down('sm')} {
		font-size: 14px;
		margin-bottom: 16px;
	}
`;

const StartButton = styled(Button)`
	margin-top: 48px !important;
	margin-bottom: 32px;
	padding: 14px 24px;
	font-size: 16px;
	font-weight: 600;
	border-radius: 8px;

	${({ theme }) => theme.breakpoints.down('sm')} {
		margin-top: 32px !important;
		margin-bottom: 20px !important;
		padding: 12px 20px !important;
	}
`;

const PrivacyPolicySection = styled(Box)`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	margin-top: auto;

	${({ theme }) => theme.breakpoints.down('sm')} {
		margin-top: 20px;
		font-size: 12px;
	}
`;

const PrivacyPolicyText = styled.div`
	font-size: 14px;
	color: ${({ theme }) => theme.palette.text.secondary};
	text-align: center;

	${({ theme }) => theme.breakpoints.down('sm')} {
		font-size: 12px;
	}
`;

const PrivacyPolicyLink = styled.a`
	font-size: 14px;
	color: ${({ theme }) => theme.palette.primary.main};
	text-decoration: none;
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}

	${({ theme }) => theme.breakpoints.down('sm')} {
		font-size: 12px;
	}
`;
